import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Stack, Group, Text, Badge, Button } from '@repletoryreach/sdk-ui';
import {
  Alert,
  DataTable,
  EmptyState,
  EntityCardGrid,
  KpiFilterRow,
  PageHeader,
  StatusBadge,
  TableShell,
  type KpiFilterItem,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { admin } from '@repletoryreach/sdk-api';
import { FilterDrawer, type FilterField, type FilterValues } from '@repletoryreach/sdk-ui/data-display';
import { usePersistedState } from '../../lib/usePersistedState';
import { formatDateTime, formatInteger, formatRelative } from '../../lib/format';
import type { Indexed } from '../../lib/types';
import { TenantName } from '../../components/TenantName';
import { TwoLineCell } from '../../components/TwoLineCell';
import { ActiveFilterPills, type ActiveFilterPill } from '../../components/ActiveFilterPills';
import { TableFooter } from '../../components/TableFooter';
import { RowMenu } from '../../components/RowMenu';
import { useTenantMap } from '../../data/useTenants';
import { BroadcastDrawer } from './components/BroadcastDrawer';
import { SendProgress } from './components/SendProgress';
import { ALL_CHANNELS, ALL_STATES, CHANNEL_LABEL_KEYS, broadcastTitle, channelOf, doneOf, stateOf, STATE_LABEL_KEYS, STATE_TONE } from './lifecycle';
import { useT } from '../../i18n/I18nProvider';

const MUTED = 'var(--rr-color-text-muted)';

type Broadcast = admin.AdminBroadcast;
type KpiKey = 'all' | 'scheduled' | 'running' | 'paused' | 'completed' | 'failed';
const KPI_STATES: Exclude<KpiKey, 'all'>[] = ['scheduled', 'running', 'paused', 'completed', 'failed'];

export default function BroadcastsPage() {
  const t = useT();
  const nav = useNavigate();
  const { map: tenants } = useTenantMap();

  const [view, setView] = usePersistedState<ViewMode>('admin.broadcasts.view', 'table');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.broadcasts.pageSize', 25);

  const [query, setQuery] = useState('');
  const [kpi, setKpi] = useState<KpiKey | null>('all');
  const [filters, setFilters] = useState<FilterValues>({});
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Broadcast | null>(null);

  /*
   * Paged and filtered on the server (4 October 2026). This page used to take the newest 200 and filter them in
   * the browser, so an organisation's older stuck job fell off the end and every count was "of the newest 200".
   * The state chips and the Status filter both narrow by status; the channel and organisation filters go to the
   * server too. Search narrows the page in hand, by name and organisation.
   */
  const orgFilter = typeof filters.organisation === 'string' && filters.organisation ? filters.organisation : undefined;
  const channelFilter = typeof filters.channel === 'string' && filters.channel ? filters.channel : undefined;
  const statusFilter = (typeof filters.status === 'string' && filters.status) ? filters.status : (kpi && kpi !== 'all' ? kpi : undefined);
  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['admin', 'broadcasts', { org_id: orgFilter, channel: channelFilter, status: statusFilter, page, pageSize }],
    queryFn: () => admin.listBroadcasts({
      org_id: orgFilter,
      channel: channelFilter,
      status: statusFilter,
      limit: pageSize,
      offset: (page - 1) * pageSize
    }),
    placeholderData: (prev) => prev,
    staleTime: 30_000
  });
  const rows = useMemo(() => (data?.data ?? []) as Broadcast[], [data]);
  const total = data?.meta?.total ?? rows.length;

  // Each chip's count is the server's total under the same organisation and channel, one row asked for apiece.
  const countQueries = useQueries({
    queries: (['all', ...KPI_STATES] as KpiKey[]).map((k) => ({
      queryKey: ['admin', 'broadcasts', 'count', { org_id: orgFilter, channel: channelFilter, status: k }],
      queryFn: () => admin.listBroadcasts({ org_id: orgFilter, channel: channelFilter, status: k === 'all' ? undefined : k, limit: 1 }),
      staleTime: 30_000
    }))
  });
  const countOf = (i: number) => countQueries[i]?.data?.meta?.total ?? (countQueries[i]?.isPending ? '…' : 0);

  // Draft and cancelled have no chip: they are the two states an operator is
  // not watching for. Both are reachable through the Status filter.
  const kpiItems: KpiFilterItem[] = [
    { id: 'all', label: t('broadcasts.kpi.all'), value: countOf(0), tone: 'primary' },
    { id: 'scheduled', label: t('broadcasts.status.scheduled'), value: countOf(1), tone: 'inactive' },
    { id: 'running', label: t('broadcasts.status.running'), value: countOf(2), tone: 'pending' },
    { id: 'paused', label: t('broadcasts.status.paused'), value: countOf(3), tone: 'warning' },
    { id: 'completed', label: t('broadcasts.status.completed'), value: countOf(4), tone: 'healthy' },
    { id: 'failed', label: t('broadcasts.status.failed'), value: countOf(5), tone: 'down' }
  ];

  const filterFields: FilterField[] = useMemo(() => {
    // From the tenants list rather than the loaded rows: the filter is served
    // by the API now, so the response no longer carries every organisation.
    const orgs = Array.from(tenants.values())
      .map((tenant) => ({ value: tenant.id, label: tenant.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [
      {
        key: 'channel',
        label: t('broadcasts.filters.channel'),
        placeholder: t('broadcasts.filters.channelPlaceholder'),
        options: ALL_CHANNELS.map((c) => ({ value: c, label: t(CHANNEL_LABEL_KEYS[c]) }))
      },
      {
        key: 'organisation',
        label: t('broadcasts.filters.organisation'),
        placeholder: t('broadcasts.filters.organisationPlaceholder'),
        options: orgs
      },
      {
        key: 'status',
        label: t('broadcasts.filters.status'),
        placeholder: t('broadcasts.filters.statusPlaceholder'),
        options: ALL_STATES.map((code) => ({ value: code, label: t(STATE_LABEL_KEYS[code]) }))
      }
    ];
  }, [tenants, t]);

  const drawerActive = Object.values(filters).filter((v) => v && v !== 'all').length;
  const hasActiveFilters =
    Boolean(query.trim()) || (kpi !== null && kpi !== 'all') || drawerActive > 0;

  function clearAllFilters() {
    setQuery('');
    setKpi('all');
    setFilters({});
    setPage(1);
  }

  // The narrowings, spelled out. A count badge on the Filters button says how
  // many are on; these say which, and each comes off on its own.
  const pills: ActiveFilterPill[] = [];
  if (query.trim()) pills.push({ id: 'query', field: t('common.search'), value: query.trim() });
  if (kpi && kpi !== 'all') {
    pills.push({ id: 'kpi', field: t('common.view'), value: kpiItems.find((i) => i.id === kpi)?.label ?? kpi });
  }
  if (filters.organisation) {
    pills.push({
      id: 'organisation',
      field: t('broadcasts.filters.organisation'),
      value: tenants.get(filters.organisation)?.name ?? t('tenantName.unknownShort')
    });
  }
  if (filters.channel) {
    const c = filters.channel as keyof typeof CHANNEL_LABEL_KEYS;
    pills.push({ id: 'channel', field: t('broadcasts.filters.channel'), value: CHANNEL_LABEL_KEYS[c] ? t(CHANNEL_LABEL_KEYS[c]) : filters.channel });
  }
  if (filters.status) {
    const code = filters.status as keyof typeof STATE_LABEL_KEYS;
    pills.push({ id: 'status', field: t('broadcasts.filters.status'), value: STATE_LABEL_KEYS[code] ? t(STATE_LABEL_KEYS[code]) : filters.status });
  }

  function removePill(id: string) {
    setPage(1);
    if (id === 'query') return setQuery('');
    if (id === 'kpi') return setKpi('all');
    setFilters((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        broadcastTitle(r, t).toLowerCase().includes(q) ||
        (tenants.get(r.organisation_id)?.name ?? '').toLowerCase().includes(q)
    );
  }, [rows, query, tenants, t]);

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.section.messaging')}
        title={t('broadcasts.title')}
        description={t('broadcasts.description')}
        actions={
          <Stack gap="xs" align="flex-end">
            <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>
              {t('action.refresh')}
            </Button>
            <Text role="caption" c={MUTED}>{t('common.lastUpdated', { time: formatDateTime(new Date(dataUpdatedAt).toISOString()) })}</Text>
          </Stack>
        }
      />

      {/* Chips first, on the page ground: the split between running, finished
          and failing jobs is the reason an operator opens this page. */}
      <KpiFilterRow items={kpiItems} value={kpi} onChange={(next) => { setKpi((next ?? 'all') as KpiKey); setPage(1); }} />
      <ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />

      <TableShell
        title={t('broadcasts.tableTitle')}
        totalLabel={t('broadcasts.totalLabel', { shown: filtered.length, total: formatInteger(total) })}
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('broadcasts.searchPlaceholder')}
        filters={
          <Group gap="sm" wrap="nowrap">
            <FilterDrawer
              fields={filterFields}
              values={filters}
              onApply={(next) => { setFilters(next); setPage(1); }}
            />
          </Group>
        }
        view={view}
        onViewChange={setView}
        loading={isLoading}
        isEmpty={filtered.length === 0}
        empty={
          <EmptyState
            title={t('broadcasts.empty')}
            description={hasActiveFilters ? t('broadcasts.emptyFiltered') : t('broadcasts.emptyHint')}
            action={hasActiveFilters ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button> : undefined}
          />
        }
        error={error
          ? (
            <Alert color="error" title={t('broadcasts.loadError')}>
              <Group gap="sm">
                <Text role="bodySmall">{error instanceof Error ? error.message : t('common.unknownError')}</Text>
                <Button size="xs" variant="outline" onClick={() => void refetch()}>{t('action.retry')}</Button>
              </Group>
            </Alert>
          )
          : null}
        pagination={
          <TableFooter
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        }
      >
        {view === 'table' && (
          <DataTable<Indexed<Broadcast>>
            records={filtered as Indexed<Broadcast>[]}
            loading={isLoading}
            idAccessor="id"
            emptyState={t('broadcasts.tableEmpty')}
            onRowClick={(r) => setSelected(r as Broadcast)}
            columns={[
              {
                accessor: 'status',
                title: t('common.status'),
                render: (r) => {
                  const state = stateOf(r as Broadcast);
                  const rate = r.recipients_total > 0 ? (r.recipients_failed / r.recipients_total) * 100 : 0;
                  return (
                    <Stack gap={2} style={{ minWidth: 0 }}>
                      <StatusBadge status={STATE_TONE[state]} size="xs">{t(STATE_LABEL_KEYS[state])}</StatusBadge>
                      {/* A finished job with a tenth of its recipients bounced
                          is not the same event as a clean one, and the state
                          badge alone cannot say so. */}
                      {r.recipients_failed > 0 && (
                        <Text role="caption" c="var(--rr-color-error)">{t('broadcasts.cell.failedRate', { pct: rate.toFixed(1) })}</Text>
                      )}
                      {/* Why it failed as a whole (4 October 2026), as the code an operator searches the logs for. */}
                      {r.failure_code && (
                        <Text role="caption" c="var(--rr-color-error)"><code>{r.failure_code}</code></Text>
                      )}
                    </Stack>
                  );
                }
              },
              {
                accessor: 'name',
                title: t('broadcasts.columns.broadcast'),
                // The channel and what a person calls it (4 October 2026): an SMS or email broadcast has no template,
                // so this column used to be empty for them.
                render: (r) => (
                  <TwoLineCell
                    primary={broadcastTitle(r, t)}
                    secondary={t('broadcasts.cell.channelThrottle', {
                      channel: t(CHANNEL_LABEL_KEYS[channelOf(r)]),
                      tps: formatInteger(r.throttle_per_second)
                    })}
                    title={broadcastTitle(r, t)}
                  />
                )
              },
              {
                accessor: 'organisation_id',
                title: t('broadcasts.columns.tenant'),
                render: (r) => <TenantName id={r.organisation_id} />
              },
              {
                accessor: 'created_at',
                title: t('common.created'),
                sortable: true,
                render: (r) => <TwoLineCell primary={formatDateTime(r.created_at)} secondary={formatRelative(r.created_at)} />
              },
              {
                accessor: 'scheduled_at',
                title: t('broadcasts.columns.scheduled'),
                sortable: true,
                render: (r) => r.scheduled_at
                  ? <TwoLineCell primary={formatDateTime(r.scheduled_at)} secondary={formatRelative(r.scheduled_at)} />
                  : <Text role="caption" c={MUTED}>{t('broadcasts.notScheduled')}</Text>
              },
              {
                accessor: 'recipients_total',
                title: t('broadcasts.columns.recipients'),
                textAlign: 'right',
                sortable: true,
                render: (r) => formatInteger(r.recipients_total)
              },
              {
                accessor: 'recipients_sent',
                title: t('broadcasts.columns.sentTotal'),
                render: (r) => <SendProgress sent={r.recipients_sent} done={doneOf(r)} total={r.recipients_total} />
              },
              {
                accessor: 'recipients_delivered',
                title: t('broadcasts.columns.delivered'),
                textAlign: 'right',
                render: (r) => <DeliveredCell row={r} />
              },
              {
                accessor: 'recipients_failed',
                title: t('status.failed'),
                textAlign: 'right',
                render: (r) => r.recipients_failed > 0
                  ? (
                    <Badge size="xs" color="red" variant="light">
                      {t('broadcasts.failedPct', {
                        count: formatInteger(r.recipients_failed),
                        pct: r.recipients_total > 0 ? ((r.recipients_failed / r.recipients_total) * 100).toFixed(1) : '0.0'
                      })}
                    </Badge>
                  )
                  : <Text role="caption" c={MUTED}>{formatInteger(0)}</Text>
              },
              {
                accessor: 'recipients_filtered',
                title: t('broadcasts.columns.filtered'),
                textAlign: 'right',
                render: (r) => r.recipients_filtered > 0
                  ? <Badge size="xs" color="gray" variant="light">{formatInteger(r.recipients_filtered)}</Badge>
                  : <Text role="caption" c={MUTED}>{formatInteger(0)}</Text>
              },
              {
                accessor: 'actions',
                title: t('common.actions'),
                width: 64,
                textAlign: 'right',
                render: (r) => (
                  <RowMenu
                    actions={[
                      { id: 'view', label: t('broadcasts.actions.view'), onSelect: () => setSelected(r as Broadcast) },
                      { id: 'tenant', label: t('broadcasts.actions.openTenant'), onSelect: () => nav(`/tenants/${r.organisation_id}`) }
                    ]}
                  />
                )
              }
            ]}
          />
        )}
        {view === 'list' && (
          <Stack gap="sm">
            {filtered.map((r) => {
              const state = stateOf(r);
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setSelected(r)}
                  className="rr-list-row"
                >
                  <Group gap="md" align="center" wrap="nowrap" style={{ width: '100%' }}>
                    <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
                      <Group gap="sm" align="baseline" wrap="nowrap">
                        <Text role="subtitle" fw={600} truncate>{broadcastTitle(r, t)}</Text>
                        <Badge size="xs" variant="light" color="gray">{t(CHANNEL_LABEL_KEYS[channelOf(r)])}</Badge>
                        <TenantName id={r.organisation_id} subtle />
                      </Group>
                      <Text role="caption" c={MUTED}>
                        {t('broadcasts.list.meta', {
                          sent: formatInteger(r.recipients_sent),
                          total: formatInteger(r.recipients_total),
                          pct: r.recipients_total > 0 ? Math.round((doneOf(r) / r.recipients_total) * 100) : 0,
                          when: formatRelative(r.created_at)
                        })}
                      </Text>
                      <Text role="caption" c={MUTED}>
                        {r.started_at
                          ? t('broadcasts.startedRelative', { when: formatRelative(r.started_at) })
                          : r.scheduled_at
                            ? t('broadcasts.scheduledFor', { when: formatDateTime(r.scheduled_at) })
                            : t('broadcasts.notScheduled')}
                      </Text>
                    </Stack>
                    <Group gap="sm" style={{ flexShrink: 0 }}>
                      {r.recipients_failed > 0 && (
                        <Badge size="sm" variant="light" color="red">
                          {t('broadcasts.failedCount', { count: formatInteger(r.recipients_failed) })}
                        </Badge>
                      )}
                      <StatusBadge status={STATE_TONE[state]}>{t(STATE_LABEL_KEYS[state])}</StatusBadge>
                    </Group>
                  </Group>
                </button>
              );
            })}
          </Stack>
        )}
        {view === 'cards' && (
          <EntityCardGrid<Broadcast>
            records={filtered}
            idAccessor="id"
            cols={3}
            spacing="md"
            onCardClick={(r) => setSelected(r)}
            renderCard={(r) => {
              const state = stateOf(r);
              return (
                <Stack gap="md">
                  <Group justify="space-between" align="baseline" wrap="nowrap">
                    <Stack gap="xs" style={{ minWidth: 0 }}>
                      <Text role="subtitle" fw={600} truncate>{broadcastTitle(r, t)}</Text>
                      <Group gap="xs" wrap="nowrap">
                        <Badge size="xs" variant="light" color="gray">{t(CHANNEL_LABEL_KEYS[channelOf(r)])}</Badge>
                        <TenantName id={r.organisation_id} subtle />
                      </Group>
                    </Stack>
                    <StatusBadge status={STATE_TONE[state]} size="xs">{t(STATE_LABEL_KEYS[state])}</StatusBadge>
                  </Group>
                  <Group justify="space-between" align="baseline" style={{ paddingTop: 12, borderTop: '1px solid var(--rr-color-border)' }}>
                    <Stack gap="xs">
                      <Text role="caption" c={MUTED}>{t('status.sent')}</Text>
                      <Text role="bodySmall" fw={600}>{formatInteger(r.recipients_sent)} / {formatInteger(r.recipients_total)}</Text>
                    </Stack>
                    <Stack gap="xs" style={{ textAlign: 'right' }}>
                      <Text role="caption" c={MUTED}>{t('broadcasts.columns.delivered')}</Text>
                      <DeliveredCell row={r} />
                    </Stack>
                    <Stack gap="xs" style={{ textAlign: 'right' }}>
                      <Text role="caption" c={MUTED}>{t('status.failed')}</Text>
                      <Text role="bodySmall" fw={600}>{formatInteger(r.recipients_failed)}</Text>
                    </Stack>
                  </Group>
                  <SendProgress sent={r.recipients_sent} done={doneOf(r)} total={r.recipients_total} showLabel={false} />
                  <Text role="caption" c={MUTED}>
                    {r.scheduled_at
                      ? t('broadcasts.scheduledFor', { when: formatDateTime(r.scheduled_at) })
                      : t('broadcasts.createdRelative', { when: formatRelative(r.created_at) })}
                  </Text>
                </Stack>
              );
            }}
          />
        )}
      </TableShell>
      <BroadcastDrawer row={selected} onClose={() => setSelected(null)} />
    </Stack>
  );
}

/** Delivered (read included) and its rate over those handed over; on WhatsApp, read under it. */
function DeliveredCell({ row: r }: { row: Broadcast }) {
  const t = useT();
  // Nothing to report for a job that has not started (or a service from before the figure existed).
  if (r.recipients_delivered === undefined || !r.started_at) return <Text role="caption" c={MUTED}>{t('common.notAvailable')}</Text>;
  const attempted = r.recipients_sent + r.recipients_failed;
  const pct = (n: number) => (attempted > 0 ? ((n / attempted) * 100).toFixed(1) : '0.0');
  return (
    <Stack gap={0} align="flex-end">
      <Text role="bodySmall">{t('broadcasts.cell.delivered', { count: formatInteger(r.recipients_delivered), pct: pct(r.recipients_delivered) })}</Text>
      {channelOf(r) === 'whatsapp' && (r.recipients_read ?? 0) > 0 && (
        <Text role="caption" c={MUTED}>{t('broadcasts.cell.read', { count: formatInteger(r.recipients_read ?? 0), pct: pct(r.recipients_read ?? 0) })}</Text>
      )}
    </Stack>
  );
}
